import { useState, useEffect, useCallback } from 'react';
import {
    IconFileText,
    IconClock,
    IconUsers,
    IconClipboardText,
    IconWand,
    IconCalculator,
    IconSearch,
    IconBookmark,
    IconCalendar,
    IconWorld,
    IconMail,
    IconArrowRight,
    IconChevronLeft,
    IconChevronRight,
    IconAlertTriangle,
    IconBriefcase,
    IconBuildingBank,
} from '@tabler/icons-react';
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '@/components/common/PaginationBar';
import BiggaponModal from '@/components/common/BiggaponModal';
import { useDebouncedValue } from '@/lib/hooks';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import jobService from '../../../services/jobService';
import { useSavedJobsStore } from '../../../store/savedJobsStore';
import {
    toBnDigits,
    formatVacancies,
    formatBnDate,
    deadlineLabel,
    deadlineTone,
    daysUntil,
    jobLogoSrc,
} from '../../../utils/bnFormat';

const PAGE_LIMIT = 12;

// Quick-action shortcuts shown above the listing (replaces the old stat cards).
// An action with a `to` opens that page; the one without (createAd) opens the poster-editor window instead.
const QUICK_ACTIONS = [
    { key: 'formFillup', to: '/tools/form-auto-fillup', Icon: IconClipboardText, bgClass: 'bg-primary hover:bg-primary/90' },
    { key: 'createAd', Icon: IconWand, bgClass: 'bg-[#9A5B86] hover:bg-[#864b74]' },
    { key: 'ageCalculator', to: '/tools/general/age-calculator', Icon: IconCalculator, bgClass: 'bg-[#2F7D5B] hover:bg-[#276a4d]' },
];

const SORTS = ['deadline', 'morePosts', 'newest'];
const SORT_PARAM = { deadline: 'deadline', morePosts: 'posts', newest: 'new' };

// 'expired' is a pseudo-category: the listing hides expired notices everywhere
// EXCEPT this tab, which shows only them (light-red styling).
const CATEGORY_KEYS = ['all', 'government', 'defense', 'bank', 'education', 'private', 'expired'];

// Per-category accent tones (decorative — not the brand colour).
const TAG_TONES = {
    defense: 'text-[#2F7D5B] bg-[#E7F2EC] dark:bg-[#2F7D5B]/15',
    government: 'text-[#C5611F] bg-[#FBEEE4] dark:bg-[#C5611F]/15',
    bank: 'text-[#3A6FA0] bg-[#EAF0F6] dark:bg-[#3A6FA0]/15',
    education: 'text-[#B5862B] bg-[#F7F0E0] dark:bg-[#B5862B]/15',
    private: 'text-[#9A5B86] bg-[#F4E9F0] dark:bg-[#9A5B86]/15',
};

const BORDER_TONES = {
    red: 'bg-[#C0563F]',
    gold: 'bg-[#D9A93A]',
    green: 'bg-[#3C9970]',
};

const DEADLINE_TONES = {
    red: 'text-[#C0563F] bg-[#F8ECE8] dark:bg-[#C0563F]/15',
    gold: 'text-[#B5862B] bg-[#FAF2E2] dark:bg-[#B5862B]/15',
    green: 'text-[#2F7D5B] bg-[#E7F2EC] dark:bg-[#2F7D5B]/15',
};

/* ------------------------------------------------------------------ */

export default function JobListing() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [stats, setStats] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [category, setCategory] = useState('all');
    const [sort, setSort] = useState('newest');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [savedOnly, setSavedOnly] = useState(false);
    const [adEditorOpen, setAdEditorOpen] = useState(false);
    const [debouncedSearch] = useDebouncedValue(search, 350);

    const savedSet = useSavedJobsStore((s) => s.saved);
    const toggleSaved = useSavedJobsStore((s) => s.toggle);

    // Stats are independent of filters — fetch once.
    useEffect(() => {
        jobService
            .getStats()
            .then((res) => setStats(res.data))
            .catch(() => setStats(null));
    }, []);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await jobService.getJobs({
                category: category === 'expired' ? 'all' : category,
                sort: SORT_PARAM[sort],
                search: debouncedSearch || undefined,
                page,
                limit: PAGE_LIMIT,
                // Expired notices only show under the মেয়াদ শেষ tab.
                expired: category === 'expired' ? 'only' : 'exclude',
            });
            setJobs(res.data || []);
            if (res.pagination) setPagination(res.pagination);
        } catch (e) {
            setError(true);
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [category, sort, debouncedSearch, page]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    // Reset to first page whenever a filter changes.
    useEffect(() => {
        setPage(1);
    }, [category, sort, debouncedSearch]);

    const categoryCounts = stats?.categoryCounts || {};
    const savedCount = savedSet.size;
    const displayedJobs = savedOnly ? jobs.filter((j) => savedSet.has(String(j.id))) : jobs;
    const total = pagination.total;
    const totalPages = pagination.totalPages || 1;
    const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
    const rangeEnd = Math.min(pagination.page * pagination.limit, total);

    return (
        <div className="w-full font-hind py-8 text-[#1C1B19] dark:text-zinc-100">
            {/* Header */}
            <div className="mb-5 flex flex-wrap items-end justify-between gap-5">
                <div>
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {t('jobs.title')}
                    </h1>
                    <p className="mt-[5px] text-sm text-[#6E6A63] dark:text-zinc-400">
                        {t('jobs.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-2.5">
                    <span className="text-[13px] text-[#8A857B] dark:text-zinc-500">{t('jobs.today')}</span>
                </div>
            </div>

            {/* Quick action links + total listed count */}
            <div className="mb-[22px] grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
                {QUICK_ACTIONS.map(({ key, to, Icon, bgClass }) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => (to ? navigate(to) : setAdEditorOpen(true))}
                        className={`group inline-flex w-full items-center justify-center gap-2 rounded-xl px-3.5 py-2.5 text-[13.5px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:w-auto sm:justify-start sm:gap-2.5 sm:px-5 sm:py-3 sm:text-[15px] ${bgClass}`}
                    >
                        <Icon size={20} stroke={1.9} className="flex-none" />
                        <span className="truncate">{t(`jobs.actions.${key}.title`)}</span>
                        <IconArrowRight
                            size={18}
                            stroke={2}
                            className="flex-none transition-transform group-hover:translate-x-1"
                        />
                    </button>
                ))}

                {/* Useful / Sorkari links */}
                <button
                    type="button"
                    onClick={() => navigate('/dorkari-links')}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#3A6FA0] px-3.5 py-2.5 text-[13.5px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#325f8a] hover:shadow-md sm:w-auto sm:justify-start sm:gap-2.5 sm:px-5 sm:py-3 sm:text-[15px]"
                >
                    <IconBuildingBank size={20} stroke={1.9} className="flex-none" />
                    <span className="truncate">{t('navigation.sorkariLinks')}</span>
                    <IconArrowRight
                        size={18}
                        stroke={2}
                        className="flex-none transition-transform group-hover:translate-x-1"
                    />
                </button>

                {/* Total jobs listed */}
                <div className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#F4F1EB] px-4 py-3 text-sm dark:bg-zinc-800 sm:col-auto sm:ml-auto sm:justify-start">
                    <IconBriefcase size={18} stroke={1.8} className="text-[#3A6FA0]" />
                    <span className="text-lg font-bold text-[#1C1B19] dark:text-zinc-100">
                        {stats ? toBnDigits(stats.ongoing ?? 0) : '—'}
                    </span>
                    <span className="text-[#8A857B] dark:text-zinc-400">{t('jobs.stats.totalListed')}</span>
                </div>
            </div>

            {/* Search + saved + sort */}
            <div className="mb-3.5 flex flex-wrap items-center gap-3">
                <div className="relative min-w-[260px] flex-1">
                    <IconSearch
                        size={17}
                        stroke={1.8}
                        className="absolute left-[13px] top-1/2 -translate-y-1/2 text-[#A8A294]"
                    />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('jobs.searchPlaceholder')}
                        className="w-full rounded-[10px] border border-[#ECE9E3] bg-white py-[11px] pl-[38px] pr-3.5 text-sm text-[#1C1B19] outline-none placeholder:text-[#A8A294] focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    />
                </div>

                <button
                    type="button"
                    onClick={() => setSavedOnly((v) => !v)}
                    className={`flex items-center gap-[7px] rounded-[10px] border px-3.5 py-2.5 text-[12.5px] font-semibold transition-colors ${
                        savedOnly
                            ? 'border-primary bg-primary-50 text-primary dark:bg-primary-900/20'
                            : 'border-[#ECE9E3] bg-white text-[#6E6A63] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                    }`}
                >
                    <IconBookmark
                        size={15}
                        stroke={1.8}
                        className={savedOnly ? 'fill-primary text-primary' : 'text-[#A8A294]'}
                    />
                    {t('jobs.saved')}
                    <span
                        className={`rounded-full px-[7px] py-px text-[11px] font-bold ${
                            savedOnly
                                ? 'bg-white text-primary dark:bg-zinc-900'
                                : 'bg-[#F1EFEA] text-[#9C988F] dark:bg-zinc-700 dark:text-zinc-300'
                        }`}
                    >
                        {toBnDigits(savedCount)}
                    </span>
                </button>

                <div className="flex overflow-hidden rounded-[10px] border border-[#ECE9E3] bg-white dark:border-zinc-700 dark:bg-zinc-900">
                    {SORTS.map((key) => {
                        const active = sort === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setSort(key)}
                                className={`border-r border-[#ECE9E3] px-[15px] py-2.5 text-[12.5px] transition-colors last:border-r-0 dark:border-zinc-700 ${
                                    active
                                        ? 'bg-primary-50 font-semibold text-primary dark:bg-primary-900/20'
                                        : 'font-medium text-[#6E6A63] hover:bg-[#FAF8F4] dark:text-zinc-400 dark:hover:bg-zinc-800'
                                }`}
                            >
                                {t(`jobs.sort.${key}`)}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Category chips */}
            <div className="mb-[18px] flex flex-wrap gap-2">
                {CATEGORY_KEYS.map((key) => {
                    const active = category === key;
                    const count = categoryCounts[key];
                    const isExpiredTab = key === 'expired';
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setCategory(key)}
                            className={`flex items-center gap-[7px] rounded-full px-3.5 py-2 text-[13px] transition-colors ${
                                isExpiredTab
                                    ? active
                                        ? 'border-[1.5px] border-red-400 bg-red-50 font-semibold text-red-600 dark:bg-red-950/30 dark:text-red-400'
                                        : 'border border-red-200 bg-red-50/60 font-medium text-red-500 hover:bg-red-50 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-950/30'
                                    : active
                                        ? 'border-[1.5px] border-primary bg-primary-50 font-semibold text-primary dark:bg-primary-900/20'
                                        : 'border border-[#ECE9E3] bg-white font-medium text-[#56524A] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {t(`jobs.categories.${key}`)}
                            {count != null && (
                                <span
                                    className={`rounded-full px-[7px] py-px text-[11px] font-bold ${
                                        isExpiredTab
                                            ? 'bg-white text-red-500 dark:bg-zinc-900 dark:text-red-400'
                                            : active
                                                ? 'bg-white text-primary dark:bg-zinc-900'
                                                : 'bg-[#F1EFEA] text-[#9C988F] dark:bg-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {toBnDigits(count)}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Result count */}
            <div className="mb-3.5 text-[13px] text-[#8A857B] dark:text-zinc-400">
                {t('jobs.found', { count: toBnDigits(savedOnly ? displayedJobs.length : total) })}
            </div>

            {/* Body: jobs / loading / error / empty */}
            {error ? (
                <ErrorState t={t} onRetry={fetchJobs} />
            ) : loading ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <JobCardSkeleton key={i} />
                    ))}
                </div>
            ) : displayedJobs.length === 0 ? (
                <EmptyState t={t} savedOnly={savedOnly} />
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {displayedJobs.map((job) => (
                        <JobCard
                            key={job.id}
                            job={job}
                            t={t}
                            saved={savedSet.has(String(job.id))}
                            onToggleSave={() => toggleSaved(job.id)}
                            onOpen={() => navigate(`/jobs/${job.slug || job.id}`)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination — hidden in the client-side saved view */}
            {!loading && !error && !savedOnly && jobs.length > 0 && (
                <PaginationBar
                    page={pagination.page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={PAGE_LIMIT}
                    currentCount={displayedJobs.length}
                    onPage={setPage}
                    className="mt-6"
                />
            )}

            <BiggaponModal open={adEditorOpen} onClose={() => setAdEditorOpen(false)} />
        </div>
    );
}

/* ------------------------------------------------------------------ */

// variant="dashboard": the compact tile used on the Dashboard (white rounded-2xl card on a tinted section, like the rest of
// that page). Same data and actions as the listing card — only the look differs.
export function JobCard({ job, t, onOpen, saved, onToggleSave, showSave = true, variant = 'listing' }) {
    const tone = deadlineTone(job.deadlineDate);
    const expired = daysUntil(job.deadlineDate) < 0;
    const isPostal = job.applyMethod === 'postal';

    if (variant === 'dashboard') {
        return (
            <div className={`group flex flex-col gap-3 rounded-2xl border p-4 shadow-sm transition-shadow hover:shadow-md ${
                expired ? 'border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-800/40' : 'border-zinc-200/80 bg-white dark:border-zinc-700 dark:bg-zinc-900'
            }`}>
                <div className="flex items-start gap-3">
                    <div
                        onClick={onOpen}
                        className="flex h-12 w-12 flex-none cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-zinc-100 bg-zinc-50 p-1.5 dark:border-zinc-700 dark:bg-zinc-800"
                    >
                        <img src={jobLogoSrc(job)} alt="" className="h-full w-full object-contain" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div onClick={onOpen} className="line-clamp-2 cursor-pointer text-sm font-semibold leading-snug text-zinc-900 hover:text-primary dark:text-zinc-100">
                            {job.organization}
                        </div>
                        <div className="mt-0.5 line-clamp-1 text-xs text-zinc-500 dark:text-zinc-400">{job.position}</div>
                    </div>
                    <span className={`flex-none rounded-full px-2.5 py-1 text-[11px] font-semibold ${TAG_TONES[job.category]}`}>
                        {t(`jobs.categories.${job.category}`)}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                    <div className="flex items-center gap-1.5">
                        <IconUsers size={14} stroke={1.8} className="text-zinc-400" />
                        <div>
                            {t('jobs.card.posts')}{' '}
                            <b className="font-bold text-zinc-800 dark:text-zinc-100">{formatVacancies(job.totalVacancies)}</b>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {isPostal ? <IconMail size={14} stroke={1.8} className="text-zinc-400" /> : <IconWorld size={14} stroke={1.8} className="text-zinc-400" />}
                        <div>{isPostal ? 'ডাকযোগে' : 'অনলাইন আবেদন'}</div>
                    </div>
                </div>

                <div className="mt-auto flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                    <div className="min-w-0">
                        <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${DEADLINE_TONES[tone]}`}>
                            <IconClock size={13} stroke={2} />
                            {deadlineLabel(job.deadlineDate)}
                        </div>
                        <div className="mt-1 text-[11px] text-zinc-400 dark:text-zinc-500">
                            {t('jobs.card.deadlineLabel')} {formatBnDate(job.deadlineDate, { withYear: true })}
                        </div>
                    </div>
                    <div className="flex flex-none gap-1.5">
                        <button
                            type="button"
                            onClick={onOpen}
                            className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                        >
                            {t('jobs.card.details')}
                        </button>
                        {expired ? (
                            <span className="cursor-not-allowed rounded-lg bg-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-400 dark:bg-zinc-700 dark:text-zinc-500">
                                {t('jobs.card.expired')}
                            </span>
                        ) : (
                            <a
                                href={job.applyUrl || '#'}
                                target={job.applyUrl ? '_blank' : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm shadow-primary/30 transition-colors hover:bg-primary/90"
                            >
                                {t('jobs.card.apply')}
                                <IconArrowRight size={13} stroke={2} />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative flex flex-col gap-[13px] overflow-hidden rounded-[14px] border py-[18px] pl-[21px] pr-[18px] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ${
            expired
                ? 'border-[#E5E3DE] bg-[#F3F2EF] dark:border-zinc-800 dark:bg-zinc-800/40'
                : 'border-[#ECE9E3] bg-white dark:border-zinc-700 dark:bg-zinc-900'
        }`}>
            {/* Accent bar */}
            <span className={`absolute inset-y-0 left-0 w-1 ${BORDER_TONES[tone]}`} />

            {/* Top row */}
            <div className="flex items-start gap-[13px]">
                <div className="flex h-9 w-9 flex-none cursor-pointer items-center justify-center overflow-hidden rounded-[9px] text-sm font-bold text-[#56524A] dark:text-zinc-200" onClick={onOpen}>
                    <img src={jobLogoSrc(job)} alt="" className="h-full w-full object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                    <div onClick={onOpen} className="cursor-pointer text-[15.5px] font-bold leading-[1.32] text-[#1C1B19] hover:text-primary dark:text-zinc-100">
                        {job.organization}
                    </div>
                    <div className="mt-[3px] text-[12.5px] text-[#8A857B] dark:text-zinc-400">
                        {job.position}
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-[7px]">
                        <span className={`rounded-md px-[9px] py-[3px] text-[11px] font-bold ${TAG_TONES[job.category]}`}>
                            {t(`jobs.categories.${job.category}`)}
                        </span>
                        {job.secondaryTag && (
                            <span className="rounded-md bg-[#F4F2EE] px-[9px] py-[3px] text-[11px] font-semibold text-[#6E6A63] dark:bg-zinc-800 dark:text-zinc-400">
                                {job.secondaryTag}
                            </span>
                        )}
                    </div>
                </div>
                {showSave && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-pressed={saved}
                                onClick={onToggleSave}
                                className={`flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] border transition-colors ${
                                    saved
                                        ? 'border-primary bg-primary-50 text-primary dark:bg-primary-900/20'
                                        : 'border-[#ECE9E3] bg-white text-[#A8A294] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800'
                                }`}
                            >
                                <IconBookmark size={16} stroke={1.8} className={saved ? 'fill-primary' : ''} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>{saved ? t('jobs.unsave') : t('jobs.saved')}</TooltipContent>
                    </Tooltip>
                )}
            </div>

            {/* Meta row */}
            <div className="flex items-center gap-[18px] border-y border-[#F4F2EE] py-[11px] dark:border-zinc-800">
                <div className="flex items-center gap-[7px]">
                    <IconUsers size={15} stroke={1.7} className="text-[#A8A294]" />
                    <span className="text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                        {t('jobs.card.posts')}{' '}
                        <b className="font-bold text-[#1C1B19] dark:text-zinc-100">{formatVacancies(job.totalVacancies)}</b>
                    </span>
                </div>
                <div className="flex items-center gap-[7px]">
                    <IconCalendar size={15} stroke={1.7} className="text-[#A8A294]" />
                    <span className="text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                        {t('jobs.card.published')} {formatBnDate(job.publishedDate)}
                    </span>
                </div>
                <div className="flex items-center gap-[7px]">
                    {isPostal ? (
                        <IconMail size={15} stroke={1.7} className="text-[#A8A294]" />
                    ) : (
                        <IconWorld size={15} stroke={1.7} className="text-[#A8A294]" />
                    )}
                    <span className="text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                        {isPostal ? 'ডাকযোগে' : 'অনলাইন আবেদন'}
                    </span>
                </div>
            </div>

            {/* Bottom row */}
            <div className="flex items-center justify-between gap-2.5">
                <div>
                    <div className={`inline-flex items-center gap-1.5 rounded-full px-[11px] py-[5px] text-[12.5px] font-bold ${DEADLINE_TONES[tone]}`}>
                        <IconClock size={13} stroke={2} />
                        {deadlineLabel(job.deadlineDate)}
                    </div>
                    <div className="mt-[5px] text-[11.5px] text-[#A8A294] dark:text-zinc-500">
                        {t('jobs.card.deadlineLabel')} {formatBnDate(job.deadlineDate, { withYear: true })}
                    </div>
                </div>
                <div className="flex flex-none gap-2">
                    <button
                        type="button"
                        onClick={onOpen}
                        className="flex items-center gap-1.5 rounded-[9px] border border-[#E4E1DA] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#56524A] hover:bg-[#FAF8F4] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                        <IconFileText size={14} stroke={1.7} />
                        {t('jobs.card.details')}
                    </button>
                    {expired ? (
                        <span
                            className="flex cursor-not-allowed items-center gap-1.5 rounded-[9px] bg-[#E5E3DE] px-[15px] py-[9px] text-[12.5px] font-semibold text-[#A8A294] dark:bg-zinc-700 dark:text-zinc-500"
                        >
                            {t('jobs.card.expired')}
                        </span>
                    ) : (
                        <a
                            href={job.applyUrl || '#'}
                            target={job.applyUrl ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 rounded-[9px] bg-primary px-[15px] py-[9px] text-[12.5px] font-semibold text-white shadow-[0_2px_8px_rgba(230,138,0,0.25)] hover:bg-primary/90"
                        >
                            {t('jobs.card.apply')}
                            <IconArrowRight size={14} stroke={2} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function JobCardSkeleton() {
    return (
        <div className="relative flex animate-pulse flex-col gap-[13px] overflow-hidden rounded-[14px] border border-[#ECE9E3] bg-white py-[18px] pl-[21px] pr-[18px] dark:border-zinc-700 dark:bg-zinc-900">
            <span className="absolute inset-y-0 left-0 w-1 bg-[#ECE9E3] dark:bg-zinc-700" />
            <div className="flex items-start gap-[13px]">
                <div className="h-9 w-9 flex-none rounded-[9px] bg-[#F1EFEA] dark:bg-zinc-800" />
                <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-2/3 rounded bg-[#F1EFEA] dark:bg-zinc-800" />
                    <div className="h-3 w-1/2 rounded bg-[#F1EFEA] dark:bg-zinc-800" />
                    <div className="flex gap-2 pt-1">
                        <div className="h-4 w-16 rounded bg-[#F1EFEA] dark:bg-zinc-800" />
                        <div className="h-4 w-12 rounded bg-[#F1EFEA] dark:bg-zinc-800" />
                    </div>
                </div>
            </div>
            <div className="h-9 border-y border-[#F4F2EE] dark:border-zinc-800" />
            <div className="flex items-center justify-between">
                <div className="h-7 w-24 rounded-full bg-[#F1EFEA] dark:bg-zinc-800" />
                <div className="flex gap-2">
                    <div className="h-9 w-20 rounded-[9px] bg-[#F1EFEA] dark:bg-zinc-800" />
                    <div className="h-9 w-20 rounded-[9px] bg-[#F1EFEA] dark:bg-zinc-800" />
                </div>
            </div>
        </div>
    );
}

function EmptyState({ t, savedOnly }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed border-[#ECE9E3] bg-white py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
            <IconBriefcase size={36} stroke={1.5} className="text-[#C0BBB0] dark:text-zinc-600" />
            <p className="mt-3 text-sm font-medium text-[#8A857B] dark:text-zinc-400">
                {savedOnly ? t('jobs.emptySaved') : t('jobs.empty')}
            </p>
        </div>
    );
}

function ErrorState({ t, onRetry }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed border-[#ECE9E3] bg-white py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
            <IconAlertTriangle size={36} stroke={1.5} className="text-[#C0563F]" />
            <p className="mt-3 text-sm font-medium text-[#8A857B] dark:text-zinc-400">{t('jobs.error')}</p>
            <button
                type="button"
                onClick={onRetry}
                className="mt-4 rounded-[9px] bg-primary px-4 py-2 text-[13px] font-semibold text-white hover:bg-primary/90"
            >
                {t('jobs.retry')}
            </button>
        </div>
    );
}
